import React from "react";
import { Column, Conditional, Else, If, Row, Section } from "@hex-insights/core";
import {
	AcademicTerm,
	CourseSectionPeriodFilterInput,
	EmployeeDetailQuery,
	useCourseSectionListQuery,
	useHomeRoomSectionListQuery,
} from "../../../../../../Utilities";
import { AcademicTermUtilities } from "../../../../../Utilities";
import * as CourseSectionComponents from "../../../../CourseSection";
import * as CourseSectionPeriodComponents from "../../../../CourseSectionPeriod";
import * as HomeRoomSectionComponents from "../../../../HomeRoomSection";
import * as StudentComponents from "../../../../Student";
import generalStyles from "../general.module.css";
import styles from "./styles.module.css";

export type AcademicsBodyProps = {
	employee: EmployeeDetailQuery["employee"];
};

export function AcademicsBody({ employee }: AcademicsBodyProps) {
	const { loading: loadingAcademicTerms, selectedAcademicTerm } = AcademicTermUtilities.useAcademicTermsNavigation();

	return (
		<Row justify="spaced-start" className={generalStyles["body"]}>
			<Column justify="spaced-start" className={styles["side-bar"]} style={{ flexShrink: 0 }}>
				<ScheduleSection employee={employee} />
			</Column>

			<Column justify="spaced-start" style={{ flexGrow: 1, minWidth: 0 }}>
				<Conditional>
					<If condition={loadingAcademicTerms && !selectedAcademicTerm}>Loading...</If>
					<Else>
						{!!selectedAcademicTerm && (
							<React.Fragment>
								<StudentsSection employee={employee} academicTerm={selectedAcademicTerm} />
								<HomeRoomSectionsSection employee={employee} academicTerm={selectedAcademicTerm} />
								<CourseSectionsSection employee={employee} academicTerm={selectedAcademicTerm} />
							</React.Fragment>
						)}
					</Else>
				</Conditional>
			</Column>
		</Row>
	);
}

type PageSectionProps = {
	employee: EmployeeDetailQuery["employee"];
};

function ScheduleSection({ employee }: PageSectionProps) {
	const filters = React.useMemo<CourseSectionPeriodFilterInput>(
		() => ({
			courseSection: [{ teacherCourseSectionEnrollments: [{ teacherIDEQ: employee.id }] }],
		}),
		[employee.id],
	);

	return (
		<CourseSectionPeriodComponents.ScheduleTile
			filters={filters}
			Container={Section}
			className={styles["side-bar__section"] + " " + styles["schedule-section"]}
		/>
	);
}

type TermBasedSectionProps = {
	employee: EmployeeDetailQuery["employee"];
	academicTerm: Pick<AcademicTerm, "id">;
};

function HomeRoomSectionsSection({ employee, academicTerm }: TermBasedSectionProps) {
	const { loading, data } = useHomeRoomSectionListQuery({
		variables: {
			filters: {
				academicTermIDEQ: academicTerm.id,
				teacherHomeRoomSectionEnrollments: [{ teacherIDEQ: employee.id }],
			},
		},
	});
	const homeRoomSections = React.useMemo(() => data?.homeRoomSectionConnection.edges.map((e) => e.node), [data]);
	const numHomeRoomSections = homeRoomSections?.length ?? 0;

	const columns = numHomeRoomSections === 1 ? 1 : 2;
	const rows = 1;

	if (!loading && numHomeRoomSections === 0) {
		return null;
	}

	return (
		<HomeRoomSectionComponents.HomeRoomSectionsPaginatedGridTile
			isLoading={loading}
			homeRoomSections={homeRoomSections}
			columns={columns}
			rows={rows}
		/>
	);
}

function CourseSectionsSection({ employee, academicTerm }: TermBasedSectionProps) {
	const { loading, data } = useCourseSectionListQuery({
		variables: {
			filters: {
				academicTermIDEQ: academicTerm.id,
				teacherCourseSectionEnrollments: [{ teacherIDEQ: employee.id }],
			},
		},
	});
	const courseSections = React.useMemo(() => data?.courseSectionConnection.edges.map((e) => e.node), [data]);

	if (!loading && (courseSections?.length ?? 0) === 0) {
		return null;
	}

	return (
		<CourseSectionComponents.CourseSectionsPaginatedGridTile
			isLoading={loading}
			courseSections={courseSections}
			columns={2}
			rows={3}
		/>
	);
}

function StudentsSection({ employee, academicTerm }: TermBasedSectionProps) {
	const filters = React.useMemo(
		() => ({
			or: [
				{
					studentCourseSectionEnrollments: [
						{
							courseSection: [
								{
									academicTermIDEQ: academicTerm.id,
									teacherCourseSectionEnrollments: [{ teacherIDEQ: employee.id }],
								},
							],
						},
					],
				},
				{
					studentHomeRoomSectionEnrollments: [
						{
							homeRoomSection: [
								{
									academicTermIDEQ: academicTerm.id,
									teacherHomeRoomSectionEnrollments: [{ teacherIDEQ: employee.id }],
								},
							],
						},
					],
				},
			],
		}),
		[employee.id, academicTerm.id],
	);

	return <StudentComponents.ControlledStudentsRowTile filters={filters} />;
}
